import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { ManualAutoTag } from "@/pages/mill/schedule/hooks/useTableData";
import { getMomentTz, TimeService } from "@dexteel/mesf-core";
import {
	Add,
	DeleteOutline,
	ChatBubble,
	ChatBubbleOutline,
	Check,
	Close,
	EditOutlined,
	RestoreFromTrash,
	TaskAlt,
} from "@mui/icons-material";
import {
	Box,
	Button,
	ButtonBase,
	Checkbox,
	IconButton,
	Paper,
	Popover,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import { deepPurple } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";
import { DateTimePicker } from "@mui/x-date-pickers";
import { Moment } from "moment";
import { Fragment, useState } from "react";
import {
	BundleAction,
	BundleDetail,
	ScrapAction,
	ScrapEntry,
} from "../../models/FinishedBundle";
import { formatLength, formatWeight, numCellSx } from "../../utils/utils";
import { BundleForm } from "./BundleForm";
import {
	FILTERS,
	getBundleStatus,
	HeatGroup,
	matchesFilter,
	Pill,
	suggestHeatIds,
} from "./StackerReviewComponents";

const moment = getMomentTz();

const InlinePieces = ({
	value,
	canEdit,
	disabled,
	tooltip,
	testId,
	onSave,
}: {
	value: number | null;
	canEdit: boolean;
	disabled: boolean;
	tooltip: string;
	testId: string;
	onSave: (pieces: number) => void;
}) => {
	const [editing, setEditing] = useState<string | null>(null);

	const save = () => {
		const n = Number(editing);
		if (
			editing === null ||
			editing === "" ||
			Number.isNaN(n) ||
			n < 0 ||
			!Number.isInteger(n)
		)
			return;
		if (n !== (value ?? 0)) onSave(n);
		setEditing(null);
	};

	if (editing !== null)
		return (
			<TextField
				size="small"
				type="number"
				value={editing}
				autoFocus
				onFocus={(e) => e.target.select()}
				onChange={(e) => setEditing(e.target.value)}
				onBlur={() => setEditing(null)}
				onKeyDown={(e) => {
					if (e.key === "Enter") save();
					if (e.key === "Escape") setEditing(null);
				}}
				inputProps={{ min: 0, "data-testid": `${testId}-input` }}
				sx={{
					width: 64,
					my: -0.5,
					"& .MuiInputBase-input": {
						py: 0.5,
						px: 1,
						fontSize: 14,
						textAlign: "right",
						MozAppearance: "textfield",
					},
					"& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
						{ WebkitAppearance: "none", margin: 0 },
				}}
			/>
		);

	if (!canEdit) return <>{value ?? "-"}</>;

	return (
		<Tooltip title={tooltip}>
			<ButtonBase
				disabled={disabled}
				onClick={() => setEditing(value ? String(value) : "")}
				aria-label="Edit pieces"
				data-testid={testId}
				sx={{
					font: "inherit",
					color: "inherit",
					gap: 0.5,
					px: 0.75,
					py: 0.25,
					borderRadius: 1,
					"& svg": { color: "text.disabled" },
					"&:hover": { backgroundColor: "action.hover" },
					"&:hover svg": { color: "primary.main" },
				}}
			>
				{value ?? "-"}
				<EditOutlined sx={{ fontSize: 14 }} />
			</ButtonBase>
		</Tooltip>
	);
};

const inShift = (m: Moment, shift: { start: Date; end: Date } | null) =>
	!shift || (!m.isBefore(shift.start) && m.isBefore(shift.end));

const BundleTimePicker = ({
	value,
	autoFocus = false,
	testId,
	onChange,
	onAccept,
	onEnter,
	onEscape,
}: {
	value: Moment | null;
	autoFocus?: boolean;
	testId: string;
	onChange: (value: Moment | null) => void;
	onAccept?: (value: Moment) => void;
	onEnter: (value: Moment | null) => void;
	onEscape: () => void;
}) => (
	<Box
		component="span"
		onKeyDownCapture={(e) => {
			if (e.key === "Escape") onEscape();
			if (e.key !== "Enter") return;
			e.preventDefault();
			e.stopPropagation();
			onEnter(value);
		}}
		sx={{ display: "inline-flex" }}
	>
		<DateTimePicker
			value={value}
			format="MM/DD HH:mm:ss"
			timezone={TimeService.getInstance().getServerTimeZone()}
			views={["year", "month", "day", "hours", "minutes", "seconds"]}
			ampm={false}
			onChange={onChange}
			onAccept={(next) => {
				if (next?.isValid()) onAccept?.(next);
			}}
			slotProps={{
				textField: {
					size: "small",
					autoFocus,
					inputProps: { "data-testid": testId },
					sx: {
						width: "fit-content",
						my: -0.5,
						position: "relative",
						zIndex: 1,
						backgroundColor: "background.paper",
						"& .MuiInputBase-root": { pr: 0.5 },
						"& .MuiInputBase-input": {
							py: 0.5,
							px: 1,
							fontSize: 14,
							flex: "none",
							width: "12.5ch",
						},
						"& .MuiInputAdornment-root": { ml: 0 },
					},
				},
				openPickerButton: { size: "small", sx: { p: 0.25 } },
				openPickerIcon: { sx: { fontSize: 16 } },
			}}
		/>
	</Box>
);

const AddBundleRow = ({
	index,
	reference,
	defaultPieces,
	shift,
	disabled,
	colSpan,
	onCreate,
	onClose,
}: {
	index: number;
	reference: Date;
	defaultPieces: number;
	shift: { start: Date; end: Date } | null;
	disabled: boolean;
	colSpan: number;
	onCreate: (timestamp: Date, pieces: number) => void;
	onClose: () => void;
}) => {
	const [draft, setDraft] = useState({
		time: (inShift(moment(), shift)
			? moment()
			: moment(reference)) as Moment | null,
		pieces: defaultPieces ? String(defaultPieces) : "",
	});

	const label = `#${String(index).padStart(2, "0")}`;
	const pieces = Number(draft.pieces);
	const timestamp = draft.time?.isValid() ? draft.time : null;
	const valid =
		!!timestamp &&
		draft.pieces !== "" &&
		Number.isInteger(pieces) &&
		pieces >= 0;

	const save = (time = timestamp) => {
		if (!valid || !time?.isValid()) return;
		onCreate(time.toDate(), pieces);
		onClose();
	};
	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") save();
		if (e.key === "Escape") onClose();
	};

	return (
		<TableRow
			data-testid="bundle-add-row"
			sx={(t) => ({
				"& td": {
					height: 48,
					py: 0,
					backgroundColor: alpha(t.palette.primary.main, 0.04),
				},
			})}
		>
			<TableCell sx={{ color: "text.secondary" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
					<ManualAutoTag isManual size={21} />
					{label}
				</Box>
			</TableCell>
			<TableCell>
				<BundleTimePicker
					value={draft.time}
					autoFocus
					testId="bundle-add-time"
					onChange={(time) => setDraft({ ...draft, time })}
					onEnter={(time) => save(time)}
					onEscape={onClose}
				/>
			</TableCell>
			<TableCell sx={numCellSx}>
				<TextField
					size="small"
					type="number"
					value={draft.pieces}
					onChange={(e) => setDraft({ ...draft, pieces: e.target.value })}
					onKeyDown={onKeyDown}
					inputProps={{ min: 0, "data-testid": "bundle-add-pieces" }}
					sx={{
						width: 64,
						"& .MuiInputBase-input": {
							py: 0.5,
							px: 1,
							fontSize: 14,
							textAlign: "right",
							MozAppearance: "textfield",
						},
						"& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
							{ WebkitAppearance: "none", margin: 0 },
					}}
				/>
			</TableCell>
			<TableCell colSpan={colSpan - 4}>
				<Typography variant="caption" color="text.secondary">
					Enter adds · Esc cancels
				</Typography>
			</TableCell>
			<TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
				<IconButton size="small" onClick={onClose} aria-label="Cancel">
					<Close fontSize="small" />
				</IconButton>
				<Tooltip title="Add bundle">
					<span>
						<IconButton
							size="small"
							color="primary"
							disabled={!valid || disabled}
							onClick={() => save()}
							aria-label="Add bundle"
							data-testid="bundle-add-save"
						>
							<Check fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			</TableCell>
		</TableRow>
	);
};

const InlineTime = ({
	value,
	canEdit,
	disabled,
	onSave,
}: {
	value: Date;
	canEdit: boolean;
	disabled: boolean;
	onSave: (timestamp: Date) => void;
}) => {
	const [editing, setEditing] = useState<Moment | null | undefined>(undefined);
	const current = moment(value);

	const save = (next: Moment | null | undefined) => {
		if (!next?.isValid()) return;
		if (!next.isSame(current, "second")) onSave(next.toDate());
		setEditing(undefined);
	};

	if (editing !== undefined)
		return (
			<BundleTimePicker
				value={editing}
				autoFocus
				testId="bundle-row-time-input"
				onChange={setEditing}
				onAccept={save}
				onEnter={save}
				onEscape={() => setEditing(undefined)}
			/>
		);

	const label = current.format("HH:mm:ss");
	if (!canEdit) return <>{label}</>;

	return (
		<Tooltip title="Correct the date and time">
			<ButtonBase
				disabled={disabled}
				onClick={() => setEditing(current)}
				aria-label="Edit time"
				data-testid="bundle-row-time"
				sx={{
					font: "inherit",
					color: "inherit",
					gap: 0.5,
					px: 0.75,
					py: 0.25,
					ml: -0.75,
					borderRadius: 1,
					"& svg": { color: "text.disabled" },
					"&:hover": { backgroundColor: "action.hover" },
					"&:hover svg": { color: "primary.main" },
				}}
			>
				{label}
				<EditOutlined sx={{ fontSize: 14 }} />
			</ButtonBase>
		</Tooltip>
	);
};

const CommentButton = ({
	value,
	canEdit,
	disabled,
	placeholder,
	testId,
	onSave,
}: {
	value: string | null;
	canEdit: boolean;
	disabled: boolean;
	placeholder: string;
	testId: string;
	onSave: (text: string) => void;
}) => {
	const [editing, setEditing] = useState<string | null>(null);
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);

	const save = () => {
		if (editing === null) return;
		const next = editing.trim();
		if (next !== (value ?? "")) onSave(next);
		setEditing(null);
	};

	return (
		<>
			<Tooltip
				title={
					value ||
					(canEdit && !disabled ? `Add ${placeholder.toLowerCase()}` : "")
				}
			>
				<span>
					<IconButton
						size="small"
						disabled={!canEdit || disabled}
						color={value ? "primary" : "default"}
						onClick={(e) => {
							setAnchor(e.currentTarget);
							setEditing(value ?? "");
						}}
						aria-label={placeholder}
						data-testid={testId}
						sx={{
							visibility: value || canEdit ? "visible" : "hidden",
							"&.Mui-disabled": {
								color: value ? "primary.main" : undefined,
							},
							...(value ? {} : { opacity: 0.4 }),
						}}
					>
						{value ? (
							<ChatBubble fontSize="small" />
						) : (
							<ChatBubbleOutline fontSize="small" />
						)}
					</IconButton>
				</span>
			</Tooltip>
			<Popover
				open={editing !== null}
				anchorEl={anchor}
				onClose={() => setEditing(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<Box sx={{ p: 1.5, width: 320 }}>
					<TextField
						size="small"
						fullWidth
						multiline
						maxRows={4}
						value={editing ?? ""}
						autoFocus
						placeholder={placeholder}
						onChange={(e) => setEditing(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter" && !e.shiftKey) {
								e.preventDefault();
								save();
							}
							if (e.key === "Escape") setEditing(null);
						}}
						inputProps={{ maxLength: 500, "data-testid": `${testId}-input` }}
					/>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ display: "block", mt: 0.75 }}
					>
						Enter saves · Esc cancels · Empty clears
					</Typography>
				</Box>
			</Popover>
		</>
	);
};

const BundleRow = ({
	b,
	index,
	canEdit,
	selected,
	disabled,
	onSelect,
	onAction,
	onConfirmShift,
	onEdit,
}: {
	b: BundleDetail;
	index: number;
	canEdit: boolean;
	selected: boolean;
	disabled: boolean;
	onSelect: (checked: boolean) => void;
	onAction: (action: BundleAction) => void;
	onConfirmShift: (timestamp: Date, run: () => void) => void;
	onEdit: () => void;
}) => {
	const status = getBundleStatus(b);

	return (
		<TableRow
			hover
			selected={selected}
			data-testid={`finished-bundle-${b.BundleId}`}
			sx={{
				"& .bundle-row-actions > *": {
					opacity: 0,
					transition: "opacity 120ms",
				},
				"&:hover .bundle-row-actions > *, & .bundle-row-actions:focus-within > *":
					{ opacity: 1 },
				"& .bundle-row-actions > .bundle-row-check": { opacity: 1 },
				"& td": {
					height: 48,
					py: 0,
					fontVariantNumeric: "tabular-nums",
					...(b.IsActive ? {} : { color: "text.disabled" }),
				},
			}}
		>
			<TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap", pr: 0 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
					<Box
						component="span"
						sx={{
							display: "inline-flex",
							visibility: b.IsManual ? "visible" : "hidden",
						}}
					>
						<ManualAutoTag isManual size={21} />
					</Box>
					#{String(index).padStart(2, "0")}
					{canEdit && (
						<Checkbox
							size="small"
							checked={selected}
							disabled={disabled}
							onChange={(e) => onSelect(e.target.checked)}
							inputProps={{ "aria-label": "Select bundle" }}
						/>
					)}
				</Box>
			</TableCell>
			<TableCell sx={{ fontSize: 14 }}>
				<InlineTime
					value={b.Timestamp}
					canEdit={canEdit && b.PlcTimestamp == null}
					disabled={disabled}
					onSave={(timestamp) =>
						onConfirmShift(timestamp, () =>
							onAction({ type: "edit", ids: [b.BundleId], timestamp }),
						)
					}
				/>
			</TableCell>
			<TableCell sx={numCellSx}>
				<InlinePieces
					value={b.Pieces}
					canEdit={canEdit}
					disabled={disabled}
					tooltip="Correct pieces (short bundle, scrap)"
					testId="bundle-row-pieces"
					onSave={(pieces) =>
						onAction({ type: "edit", ids: [b.BundleId], pieces })
					}
				/>
			</TableCell>
			<TableCell sx={numCellSx}>{formatWeight(b.Tons)}</TableCell>
			<TableCell sx={{ maxWidth: 0 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						minWidth: 0,
					}}
				>
					{status && (
						<Tooltip title={status.tooltip ?? ""}>
							<span>
								<Pill label={status.label} tone={status.tone} filled />
							</span>
						</Tooltip>
					)}
				</Box>
			</TableCell>
			<TableCell align="center">
				<CommentButton
					value={b.Comment}
					canEdit={canEdit}
					disabled={disabled}
					placeholder="Comment"
					testId="bundle-row-comment"
					onSave={(comment) =>
						onAction({ type: "edit", ids: [b.BundleId], comment })
					}
				/>
			</TableCell>
			{canEdit && (
				<TableCell
					align="center"
					className="bundle-row-actions"
					sx={{ whiteSpace: "nowrap" }}
				>
					{/* Checked: always visible while the bundle has a flag to review (edit / delete show on hover) */}
					{status?.checkable && (
						<Tooltip title="Checked: the bundle is right as it is">
							<span className="bundle-row-check">
								<IconButton
									size="small"
									color="success"
									disabled={disabled}
									onClick={() => onAction({ type: "check", ids: [b.BundleId] })}
									aria-label="Mark bundle checked"
									data-testid="bundle-row-check"
								>
									<TaskAlt fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					)}
					<Tooltip title="Edit section, heat, length or pieces of this bundle">
						<span>
							<IconButton
								size="small"
								disabled={disabled}
								onClick={onEdit}
								aria-label="Edit bundle"
								data-testid="bundle-row-edit"
							>
								<EditOutlined fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip
						title={
							b.IsActive
								? "Delete: the bundle stops counting in Finished"
								: "Restore: the bundle was real and counts again"
						}
					>
						<span>
							<IconButton
								size="small"
								color={b.IsActive ? "default" : "success"}
								disabled={disabled}
								onClick={() =>
									onAction({
										type: "active",
										ids: [b.BundleId],
										isActive: !b.IsActive,
									})
								}
								aria-label={b.IsActive ? "Delete bundle" : "Restore bundle"}
								data-testid={
									b.IsActive ? "bundle-row-delete" : "bundle-row-restore"
								}
							>
								{b.IsActive ? (
									<DeleteOutline fontSize="small" />
								) : (
									<RestoreFromTrash fontSize="small" />
								)}
							</IconButton>
						</span>
					</Tooltip>
				</TableCell>
			)}
		</TableRow>
	);
};

const ScrapRow = ({
	entry,
	heat,
	canEdit,
	disabled,
	onScrapAction,
	onClose,
}: {
	entry: ScrapEntry | null;
	heat: HeatGroup;
	canEdit: boolean;
	disabled: boolean;
	onScrapAction: (action: ScrapAction) => void;
	onClose?: () => void;
}) => {
	const [draft, setDraftState] = useState<{
		pieces: string;
		reason: string;
	} | null>(entry ? null : { pieces: "", reason: "" });
	const setDraft = (next: { pieces: string; reason: string } | null) => {
		setDraftState(next);
		if (!next) onClose?.();
	};
	const at = entry?.UpdatedAt ?? entry?.CreatedAt;
	const pieces = draft ? Number(draft.pieces) : 0;
	const valid =
		!!draft &&
		draft.pieces !== "" &&
		Number.isInteger(pieces) &&
		pieces >= 0 &&
		(entry !== null || pieces > 0);

	const save = () => {
		if (!draft || !valid) return;
		const reason = draft.reason.trim();
		if (!entry)
			onScrapAction({
				type: "create",
				heatId: heat.HeatId,
				sectionId: heat.SectionId,
				lengthFt: heat.LengthFt,
				pieces,
				reason: reason || null,
			});
		else if (pieces === 0)
			onScrapAction({ type: "remove", ids: [entry.ScrapPiecesId] });
		else if (pieces !== entry.Pieces || reason !== (entry.Reason ?? ""))
			onScrapAction({
				type: "edit",
				id: entry.ScrapPiecesId,
				pieces,
				reason,
			});
		setDraft(null);
	};
	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") save();
		if (e.key === "Escape") setDraft(null);
	};

	return (
		<TableRow
			data-testid={entry ? `scrap-entry-${entry.ScrapPiecesId}` : "scrap-new"}
			sx={
				draft
					? undefined
					: {
							"& .scrap-row-actions > *": {
								opacity: 0,
								transition: "opacity 120ms",
							},
							"&:hover .scrap-row-actions > *, & .scrap-row-actions:focus-within > *":
								{ opacity: 1 },
						}
			}
		>
			<TableCell
				sx={{
					whiteSpace: "nowrap",
					overflow: "hidden",
					textOverflow: "ellipsis",
				}}
			>
				{entry?.UpdatedBy ?? "-"}
			</TableCell>
			<TableCell sx={{ fontSize: 14 }}>
				{at ? moment(at).format("MM/DD HH:mm") : "-"}
			</TableCell>
			<TableCell sx={numCellSx}>
				{draft ? (
					<TextField
						size="small"
						type="number"
						value={draft.pieces}
						autoFocus
						onFocus={(e) => e.target.select()}
						onChange={(e) => setDraft({ ...draft, pieces: e.target.value })}
						onKeyDown={onKeyDown}
						inputProps={{ min: 0, "data-testid": "scrap-pieces-input" }}
						sx={{
							width: 80,
							"& .MuiInputBase-input": {
								py: 0.5,
								px: 1,
								fontSize: 14,
								textAlign: "right",
								MozAppearance: "textfield",
							},
							"& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
								{
									WebkitAppearance: "none",
									margin: 0,
								},
							my: -0.5,
						}}
					/>
				) : (
					(entry?.Pieces.toLocaleString() ?? "-")
				)}
			</TableCell>
			<TableCell sx={numCellSx}>
				{entry ? formatWeight(entry.Tons || 0) : "-"}
			</TableCell>
			<TableCell colSpan={2} sx={{ maxWidth: 0 }}>
				{draft ? (
					<TextField
						size="small"
						fullWidth
						value={draft.reason}
						placeholder="Comment"
						onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
						onKeyDown={onKeyDown}
						inputProps={{ maxLength: 500, "data-testid": "scrap-reason-input" }}
						sx={{
							my: -0.5,
							"& .MuiInputBase-input": { py: 0.5, px: 1, fontSize: 14 },
						}}
					/>
				) : (
					<Tooltip title={entry?.Reason ?? ""}>
						<Typography variant="body2" noWrap>
							{entry?.Reason || ""}
						</Typography>
					</Tooltip>
				)}
			</TableCell>
			{canEdit && (
				<TableCell
					align="center"
					className="scrap-row-actions"
					sx={{ whiteSpace: "nowrap" }}
				>
					{draft ? (
						<>
							<IconButton
								size="small"
								onClick={() => setDraft(null)}
								aria-label="Cancel"
							>
								<Close fontSize="small" />
							</IconButton>
							<Tooltip title={entry ? "Save (0 pieces removes it)" : "Save"}>
								<span>
									<IconButton
										size="small"
										color="primary"
										disabled={!valid || disabled}
										onClick={save}
										aria-label="Save scrap"
										data-testid="scrap-save"
									>
										<Check fontSize="small" />
									</IconButton>
								</span>
							</Tooltip>
						</>
					) : (
						<Tooltip title={entry ? "Edit scrap" : "Add scrap"}>
							<span>
								<IconButton
									size="small"
									disabled={disabled}
									onClick={() =>
										setDraft({
											pieces: entry ? String(entry.Pieces) : "",
											reason: entry?.Reason ?? "",
										})
									}
									aria-label={entry ? "Edit scrap" : "Add scrap"}
									data-testid="scrap-edit"
								>
									<EditOutlined fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					)}
				</TableCell>
			)}
		</TableRow>
	);
};

type Props = {
	heat: HeatGroup;
	rows: BundleDetail[];
	scrap: ScrapEntry[];
	canEdit: boolean;
	disabled: boolean;
	shift: { start: Date; end: Date } | null;
	shiftId?: number;
	shiftHeatIds: number[];
	shiftBundles: BundleDetail[];
	onAction: (action: BundleAction) => void;
	onScrapAction: (action: ScrapAction) => void;
};

export const HeatBundlesPanel = ({
	heat,
	rows,
	scrap,
	canEdit,
	disabled,
	shift,
	shiftId,
	shiftHeatIds,
	shiftBundles,
	onAction,
	onScrapAction,
}: Props) => {
	const [filter, setFilter] = useState<
		"active" | "all" | "review" | "manual" | "scrap"
	>("active");
	const [addingScrap, setAddingScrap] = useState(false);
	const [selected, setSelected] = useState<Set<number>>(new Set());
	const [adding, setAdding] = useState(false);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [pendingMove, setPendingMove] = useState<{
		timestamp: Date;
		run: () => void;
	} | null>(null);

	const confirmShift = (timestamp: Date, run: () => void) => {
		if (inShift(moment(timestamp), shift)) run();
		else setPendingMove({ timestamp, run });
	};

	const count = (f: "active" | "all" | "review" | "manual") =>
		rows.filter((b) => matchesFilter(b, f)).length;
	const activeFilter =
		filter === "scrap"
			? canEdit || scrap.length > 0
				? "scrap"
				: "all"
			: filter !== "all" && count(filter) === 0
				? "all"
				: filter;
	const showScrap = activeFilter === "scrap";
	const indexed = rows.map((b, i) => ({ b, index: i + 1 }));
	const visible =
		activeFilter === "scrap"
			? []
			: indexed.filter(({ b }) => matchesFilter(b, activeFilter));
	const scrapPieces = scrap.reduce((sum, e) => sum + e.Pieces, 0);
	const scrapTotal = heat.pieces + scrapPieces;
	const yieldPct = scrapTotal ? (heat.pieces / scrapTotal) * 100 : 0;

	const chosen = rows.filter((b) => selected.has(b.BundleId));
	const ids = chosen.map((b) => b.BundleId);
	// Selected bundles flagged by the tracking that nobody reviewed (heat to check / mill stopped)
	const toCheck = chosen.filter((b) => b.MillStoppedToCheck || b.HeatToCheck);
	const allVisibleSelected =
		visible.length > 0 && visible.every(({ b }) => selected.has(b.BundleId));
	const toggle = (bundleIds: number[], checked: boolean) => {
		const next = new Set(selected);
		for (const id of bundleIds) {
			if (checked) next.add(id);
			else next.delete(id);
		}
		setSelected(next);
	};
	const act = (action: BundleAction) => {
		onAction(action);
		setSelected(new Set());
	};

	const colSpan = canEdit ? 7 : 6;
	const heatName = heat.HeatName || "-";

	return (
		<Paper
			elevation={2}
			sx={{
				overflow: "hidden",
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
			}}
			data-testid="stacker-heat-bundles"
		>
			<Box
				sx={{
					flexShrink: 0,
					px: 2.5,
					py: 1.5,
					borderBottom: "1px solid",
					borderColor: "divider",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 2,
					minHeight: 72,
				}}
			>
				<Box sx={{ minWidth: 0 }}>
					<Box
						sx={{
							display: "flex",
							alignItems: "baseline",
							columnGap: 2.5,
							overflow: "hidden",
							whiteSpace: "nowrap",
						}}
					>
						{[
							{ label: "Heat", value: heatName },
							{ label: "Section", value: heat.SectionName },
							{ label: "Grade", value: heat.GradeName || "-" },
							{ label: "Length", value: formatLength(heat.LengthInches) },
						].map(({ label, value }) => (
							<Box
								key={label}
								sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}
							>
								<Typography
									component="span"
									variant="body2"
									color="text.disabled"
									sx={{ fontWeight: 500 }}
								>
									{label}
								</Typography>
								<Typography
									component="span"
									variant="h6"
									sx={{ fontWeight: 700 }}
								>
									{value}
								</Typography>
							</Box>
						))}
					</Box>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ fontVariantNumeric: "tabular-nums" }}
					>
						{heat.bundles.toLocaleString()} bundles ·{" "}
						{heat.pieces.toLocaleString()} pieces · {formatWeight(heat.weight)}{" "}
						t
					</Typography>
				</Box>
				{scrapPieces > 0 && (
					<Tooltip
						title={`Yield ${yieldPct.toFixed(1)} % · click to see the scrap`}
					>
						<ButtonBase
							onClick={() => {
								setFilter("scrap");
								setSelected(new Set());
								setAdding(false);
							}}
							data-testid="heat-scrap-chip"
							sx={{
								flexShrink: 0,
								gap: 0.75,
								px: 1.5,
								py: 0.5,
								borderRadius: 4,
								fontSize: 14,
								fontWeight: 600,
								fontVariantNumeric: "tabular-nums",
								color: deepPurple[700],
								border: `1px solid ${alpha(deepPurple[400], 0.3)}`,
								backgroundColor: alpha(deepPurple[400], 0.1),
								"&:hover": { backgroundColor: alpha(deepPurple[400], 0.18) },
							}}
						>
							{scrapPieces.toLocaleString()} scrap pcs
						</ButtonBase>
					</Tooltip>
				)}
			</Box>

			<Box
				sx={{
					flexShrink: 0,
					px: 2.5,
					py: 1.25,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 2,
					flexWrap: "wrap",
				}}
			>
				<ToggleButtonGroup
					exclusive
					size="small"
					value={activeFilter}
					onChange={(
						_,
						value: "active" | "all" | "review" | "manual" | "scrap" | null,
					) => {
						if (!value) return;
						setFilter(value);
						setSelected(new Set());
						setAdding(false);
						setAddingScrap(false);
					}}
					sx={{
						backgroundColor: "grey.100",
						p: 0.5,
						borderRadius: 1.5,
						gap: 0.5,
						"& .MuiToggleButtonGroup-grouped": {
							border: "0 !important",
							borderRadius: "6px !important",
							m: "0 !important",
							textTransform: "none",
							px: 1.5,
							py: 0.5,
							fontWeight: 500,
							color: "text.secondary",
							"&:hover": { backgroundColor: "action.hover" },
						},
						"& .MuiToggleButtonGroup-grouped.Mui-disabled": {
							color: "text.disabled",
							backgroundColor: "transparent",
						},
						"& .MuiToggleButtonGroup-grouped.Mui-selected, & .MuiToggleButtonGroup-grouped.Mui-selected:hover":
							{
								backgroundColor: "background.paper",
								color: "primary.main",
								boxShadow: 1,
							},
					}}
				>
					{FILTERS.filter((f) => f.always || count(f.key) > 0).map((f) => (
						<ToggleButton
							key={f.key}
							value={f.key}
							disabled={f.key !== "all" && count(f.key) === 0}
							data-testid={`bundle-filter-${f.key}`}
						>
							{f.label}
							<Box component="span" sx={{ ml: 0.75, opacity: 0.7 }}>
								{count(f.key)}
							</Box>
						</ToggleButton>
					))}
					{(canEdit || scrap.length > 0) && (
						<ToggleButton
							value="scrap"
							data-testid="bundle-filter-scrap"
							sx={{
								"&.Mui-selected, &.Mui-selected:hover": {
									color: `${deepPurple[700]} !important`,
								},
							}}
						>
							Scrap
							<Box component="span" sx={{ ml: 0.75, opacity: 0.7 }}>
								{scrap.length}
							</Box>
						</ToggleButton>
					)}
				</ToggleButtonGroup>
				{canEdit && (
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ textAlign: "right", lineHeight: 1.5 }}
					>
						{showScrap
							? "Edit a row to change pieces or comment"
							: "Click the time or pieces to correct them"}
						<br />
						Enter saves · Esc cancels
					</Typography>
				)}
			</Box>

			<TableContainer
				sx={{
					flex: "1 1 auto",
					minHeight: 0,
					borderTop: "1px solid",
					borderColor: "divider",
				}}
			>
				<Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
					<colgroup>
						{(canEdit
							? [128, 140, 90, 110, undefined, 56, 120]
							: [88, 130, 90, 110, undefined, 56]
						).map((width, i) => (
							<col key={i} style={{ width }} />
						))}
					</colgroup>
					<TableHead>
						<TableRow sx={{ "& th": { height: 40, py: 0 } }}>
							{showScrap ? (
								<>
									<TableCell>By</TableCell>
									<TableCell>Time</TableCell>
									<TableCell sx={numCellSx}>Pieces</TableCell>
									<TableCell sx={numCellSx}>Weight (t)</TableCell>
									<TableCell colSpan={2}>Comments</TableCell>
									{canEdit && <TableCell />}
								</>
							) : canEdit ? (
								<TableCell sx={{ whiteSpace: "nowrap", pr: 0 }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										<Box
											component="span"
											sx={{
												visibility: "hidden",
												display: "inline-flex",
												alignItems: "center",
												gap: 0.75,
											}}
										>
											<ManualAutoTag isManual size={21} />
											#00
										</Box>
										<Checkbox
											size="small"
											checked={allVisibleSelected}
											indeterminate={chosen.length > 0 && !allVisibleSelected}
											disabled={disabled || visible.length === 0}
											onChange={(e) =>
												toggle(
													visible.map(({ b }) => b.BundleId),
													e.target.checked,
												)
											}
											inputProps={{ "aria-label": "Select all bundles" }}
											data-testid="bundle-select-all"
										/>
									</Box>
								</TableCell>
							) : (
								<TableCell>#</TableCell>
							)}
							{showScrap ? null : chosen.length > 0 ? (
								<TableCell colSpan={colSpan - 1} sx={{ py: 0 }}>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.5,
											"& .MuiButton-root": { textTransform: "none" },
										}}
										data-testid="bundle-selection-bar"
									>
										<Typography
											variant="body2"
											sx={{ fontWeight: 600, mr: 1.5 }}
										>
											{chosen.length} selected
										</Typography>
										<Button
											size="small"
											color="success"
											startIcon={<RestoreFromTrash />}
											disabled={disabled || chosen.every((b) => b.IsActive)}
											onClick={() =>
												act({ type: "active", ids, isActive: true })
											}
											data-testid="bundle-activate-selected"
										>
											Restore
										</Button>
										<Button
											size="small"
											color="error"
											startIcon={<DeleteOutline />}
											disabled={disabled || chosen.every((b) => !b.IsActive)}
											onClick={() =>
												act({ type: "active", ids, isActive: false })
											}
											data-testid="bundle-deactivate-selected"
										>
											Delete
										</Button>
										<Button
											size="small"
											color="success"
											startIcon={<TaskAlt />}
											disabled={disabled || toCheck.length === 0}
											onClick={() =>
												act({
													type: "check",
													ids: toCheck.map((b) => b.BundleId),
												})
											}
											data-testid="bundle-check-selected"
										>
											Mark checked{toCheck.length ? ` (${toCheck.length})` : ""}
										</Button>
									</Box>
								</TableCell>
							) : (
								<>
									<TableCell>Time</TableCell>
									<TableCell sx={numCellSx}>Pieces</TableCell>
									<TableCell sx={numCellSx}>Weight (t)</TableCell>
									<TableCell>Status</TableCell>
									<TableCell />
									{canEdit && <TableCell />}
								</>
							)}
						</TableRow>
					</TableHead>
					<TableBody>
						{showScrap &&
							scrap.map((e) => (
								<ScrapRow
									key={e.ScrapPiecesId}
									entry={e}
									heat={heat}
									canEdit={canEdit}
									disabled={disabled}
									onScrapAction={onScrapAction}
								/>
							))}
						{showScrap && canEdit && addingScrap && (
							<ScrapRow
								entry={null}
								heat={heat}
								canEdit={canEdit}
								disabled={disabled}
								onScrapAction={onScrapAction}
								onClose={() => setAddingScrap(false)}
							/>
						)}
						{showScrap && scrap.length === 0 && !addingScrap && (
							<TableRow>
								<TableCell
									colSpan={colSpan}
									sx={{ py: 3, textAlign: "center" }}
								>
									<Typography variant="body2" color="text.secondary">
										No scrap for this heat.
									</Typography>
								</TableCell>
							</TableRow>
						)}
						{visible.map(({ b, index }) => (
							<Fragment key={b.BundleId}>
								<BundleRow
									b={b}
									index={index}
									canEdit={canEdit}
									selected={selected.has(b.BundleId)}
									disabled={disabled}
									onSelect={(checked) => toggle([b.BundleId], checked)}
									onAction={onAction}
									onConfirmShift={confirmShift}
									onEdit={() => setEditingId(b.BundleId)}
								/>
								{editingId === b.BundleId && (
									<TableRow
										sx={(t) => ({
											"& td": {
												backgroundColor: alpha(t.palette.primary.main, 0.04),
											},
										})}
									>
										<TableCell colSpan={colSpan}>
											<BundleForm
												bundles={[b]}
												disabled={disabled}
												shiftId={shiftId}
												shiftHeatIds={shiftHeatIds}
												suggestedHeatIds={suggestHeatIds([b], shiftBundles)}
												onSave={(values) => {
													onAction({
														type: "edit",
														ids: [b.BundleId],
														...values,
													});
													setEditingId(null);
												}}
												onCancel={() => setEditingId(null)}
											/>
										</TableCell>
									</TableRow>
								)}
							</Fragment>
						))}
						{!showScrap && canEdit && adding && (
							<AddBundleRow
								index={rows.length + 1}
								reference={rows[rows.length - 1]?.Timestamp ?? new Date()}
								shift={shift}
								defaultPieces={heat.piecesPerBundle}
								disabled={disabled}
								colSpan={colSpan}
								onClose={() => setAdding(false)}
								onCreate={(timestamp, pieces) =>
									confirmShift(timestamp, () =>
										onAction({
											type: "create",
											quantity: 1,
											heatId: heat.HeatId,
											sectionId: heat.SectionId,
											lengthFt: heat.LengthFt,
											pieces,
											timestamp,
										}),
									)
								}
							/>
						)}
						{!showScrap && visible.length === 0 && !adding && (
							<TableRow>
								<TableCell
									colSpan={colSpan}
									sx={{ py: 3, textAlign: "center" }}
								>
									<Typography variant="body2" color="text.secondary">
										{rows.length > 0
											? "No bundles match this filter."
											: "No bundles for this heat."}
									</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{canEdit && (
				<Box
					sx={{
						flexShrink: 0,
						px: 2,
						py: 1,
						borderTop: "1px solid",
						borderColor: "divider",
						backgroundColor: "background.default",
					}}
				>
					<Tooltip
						title={
							heat.HeatId == null
								? "The heat of this group isn't known: fix it first"
								: ""
						}
					>
						<span>
							{showScrap ? (
								<Button
									startIcon={<Add />}
									disabled={disabled || addingScrap || heat.HeatId == null}
									onClick={() => setAddingScrap(true)}
									data-testid="scrap-add"
									sx={{
										textTransform: "none",
										fontWeight: 600,
										color: deepPurple[700],
									}}
								>
									Add scrap to heat {heatName}
								</Button>
							) : (
								<Button
									startIcon={<Add />}
									disabled={disabled || adding || heat.HeatId == null}
									onClick={() => setAdding(true)}
									data-testid="bundle-add"
									sx={{ textTransform: "none", fontWeight: 600 }}
								>
									Add bundles to heat {heatName}
								</Button>
							)}
						</span>
					</Tooltip>
				</Box>
			)}
			<ConfirmationDialogRaw
				open={pendingMove !== null}
				title="Move to another shift"
				message={
					pendingMove
						? `${moment(pendingMove.timestamp).format("MM/DD/YYYY HH:mm:ss")} is outside this shift: the bundle will move to the shift of that time and stop counting here.`
						: ""
				}
				buttons={{ no: "Cancel", yes: "Move" }}
				onClose={(value) => {
					if (value === "OK") pendingMove?.run();
					setPendingMove(null);
				}}
			/>
		</Paper>
	);
};
